import React, { useEffect, useMemo, useState } from 'react';
import { Table, Pagination, theme } from 'antd';
import type { TableProps } from 'antd';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { SURFACE_1_SOFT } from '../../theme/creamyTokens';

/** One label/value pair inside a mobile card. */
export interface IAdminCardField {
  label: string;
  value: React.ReactNode;
  /** Takes the full card width instead of sharing the two-column grid --
   * for long values (dates ranges, e-mails, references) that would wrap
   * badly in half a phone screen. */
  wide?: boolean;
}

/** The mobile shape of one row: what a table column set becomes when there
 * is no room for columns. */
export interface IAdminCardModel {
  key: string;
  /** The one thing that identifies the row at a glance (machine title, user
   * name, invoice number...). */
  title: React.ReactNode;
  /** Secondary identifier under the title (host, e-mail, dates...). */
  subtitle?: React.ReactNode;
  /** Status tag/pill, pinned top-right. */
  badge?: React.ReactNode;
  fields: IAdminCardField[];
  /** The same buttons the desktop "Acciones" column renders. */
  actions?: React.ReactNode;
}

export interface IAdminResponsiveListProps<T> extends Omit<TableProps<T>, 'dataSource'> {
  dataSource: T[];
  /** Mobile projection of a row. Only called below 768px, so sections pay
   * nothing for it on desktop. */
  toCard: (record: T) => IAdminCardModel;
  /** Rows per page on mobile (cards are much taller than table rows, so
   * this is deliberately smaller than the desktop `pagination.pageSize`). */
  mobilePageSize?: number;
}

/**
 * The admin panel's row list. On desktop/tablet it is exactly the antd
 * `<Table>` each section had before -- same columns, sorters, filters and
 * pagination, passed straight through -- and on mobile it swaps to a list
 * of creamy cards built from `toCard`, because a 8-column table on a phone
 * is either illegible or a horizontal-scroll maze.
 *
 * This only replaces the row list itself: the section's own title, filters,
 * drawers and modals stay exactly where they are, so the panel's layout
 * structure is untouched.
 */
function AdminResponsiveList<T extends object>({
  dataSource,
  toCard,
  mobilePageSize = 8,
  style,
  ...tableProps
}: IAdminResponsiveListProps<T>): React.ReactElement {
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const [page, setPage] = useState(1);

  // A filter/search change upstream replaces dataSource wholesale; staying
  // on page 4 of a list that now has 3 rows would render an empty screen.
  useEffect(() => {
    setPage(1);
  }, [dataSource]);

  const pageItems = useMemo(
    () => dataSource.slice((page - 1) * mobilePageSize, page * mobilePageSize),
    [dataSource, page, mobilePageSize],
  );

  if (!isMobile) {
    // The same card the mobile branch builds by hand, drawn once around the
    // whole table instead of once per row: a border, the app's radius, and
    // the soft lift every other surface has. Without it an antd Table sits
    // flat on the page background with square corners, which is exactly the
    // spreadsheet look the rest of the panel moved away from. Overflow is
    // hidden so the header's own rounded corners are not squared off by the
    // table underneath them.
    return (
      <div
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          ...style,
        }}
      >
        <Table<T> dataSource={dataSource} {...tableProps} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      {pageItems.map((record) => {
        const card = toCard(record);
        const visibleFields = card.fields.filter((field) => field.value !== null && field.value !== undefined && field.value !== '');
        return (
          <div
            key={card.key}
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 16,
              padding: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: token.colorText, lineHeight: 1.3 }}>{card.title}</div>
                {card.subtitle && (
                  <div style={{ fontSize: 13, color: token.colorTextSecondary, marginTop: 2 }}>{card.subtitle}</div>
                )}
              </div>
              {card.badge && <div style={{ flexShrink: 0 }}>{card.badge}</div>}
            </div>

            {visibleFields.length > 0 && (
              <div
                style={{
                  background: SURFACE_1_SOFT,
                  borderRadius: 12,
                  padding: '10px 12px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px 12px',
                }}
              >
                {visibleFields.map((field, index) => (
                  <div key={`${card.key}-${field.label}-${index}`} style={{ gridColumn: field.wide ? '1 / -1' : undefined, minWidth: 0 }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: token.colorTextTertiary }}>
                      {field.label}
                    </div>
                    <div style={{ fontSize: 14, color: token.colorText, overflowWrap: 'anywhere' }}>{field.value}</div>
                  </div>
                ))}
              </div>
            )}

            {card.actions && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{card.actions}</div>}
          </div>
        );
      })}

      {dataSource.length > mobilePageSize && (
        <Pagination
          simple
          align="center"
          current={page}
          pageSize={mobilePageSize}
          total={dataSource.length}
          onChange={setPage}
          style={{ marginTop: 4 }}
        />
      )}
    </div>
  );
}

export default AdminResponsiveList;
